import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import {
  Check,
  Copy,
  Loader2,
  Mail,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";

import { OrganizationMember } from "@/types/organization";

interface OrganizationMemberDisplay extends Omit<OrganizationMember, "user"> {
  users: {
    id: string;
    email: string;
  };
}

interface Invitation {
  id: string;
  email: string;
  organization_id: string;
  role: string;
  created_at: string;
  expires_at: string;
  token: string;
  status: string;
  accepted_at: string | null;
}

export default function OrganizationMembers() {
  const [members, setMembers] = useState<OrganizationMemberDisplay[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<
    "owner" | "admin" | "member" | "viewer"
  >("member");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [currentUser, setCurrentUser] = useState<{ id: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { currentOrganization } = useOrganization();

  useEffect(() => {
    // Fetch current user
    const fetchCurrentUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        setCurrentUser({ id: data.user.id });
      }
    };

    fetchCurrentUser();

    if (currentOrganization) {
      fetchMembers();
      fetchInvitations();

      // Set up realtime subscription for members
      const membersSubscription = supabase
        .channel(`org-members-${currentOrganization.id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "organization_members",
            filter: `organization_id=eq.${currentOrganization.id}`,
          },
          () => fetchMembers(),
        )
        .subscribe();

      // Set up realtime subscription for invitations
      const invitationsSubscription = supabase
        .channel(`org-invitations-${currentOrganization.id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "organization_invitations",
            filter: `organization_id=eq.${currentOrganization.id}`,
          },
          () => fetchInvitations(),
        )
        .subscribe();

      return () => {
        membersSubscription.unsubscribe();
        invitationsSubscription.unsubscribe();
      };
    }
  }, [currentOrganization]);

  const fetchMembers = async () => {
    if (!currentOrganization) return;

    try {
      setLoadingMembers(true);
      const { data, error } = await supabase
        .from("organization_members")
        .select("*, users(id, email)")
        .eq("organization_id", currentOrganization.id);

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error("Error fetching organization members:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load organization members",
      });
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchInvitations = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from("organization_invitations")
        .select("*")
        .eq("organization_id", currentOrganization.id)
        .is("accepted_at", null);

      if (error) throw error;
      setInvitations(data || []);
    } catch (error) {
      console.error("Error fetching organization invitations:", error);
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim() || !currentOrganization) return;

    try {
      setIsLoading(true);

      const { data, error } = await supabase.functions.invoke(
        "invite_organization_member",
        {
          body: {
            email: inviteEmail,
            organizationId: currentOrganization.id, // Include organizationId in the request
            role: inviteRole,
          },
        },
      );

      if (error) throw error;

      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${inviteEmail}`,
      });

      setInviteEmail("");
      setInviteRole("member");
      setIsInviteDialogOpen(false);
      fetchInvitations();
    } catch (error) {
      console.error("Error inviting member:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send invitation",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from("organization_invitations")
        .delete()
        .eq("id", invitationId);

      if (error) throw error;

      toast({
        title: "Invitation cancelled",
        description: "The invitation has been cancelled",
      });

      fetchInvitations();
    } catch (error) {
      console.error("Error cancelling invitation:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to cancel invitation",
      });
    }
  };

  const updateMemberRole = async (memberId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from("organization_members")
        .update({ role: newRole })
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: "Role updated",
        description: "Member role has been updated",
      });

      fetchMembers();
    } catch (error) {
      console.error("Error updating member role:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update member role",
      });
    }
  };

  const removeMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from("organization_members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: "Member removed",
        description: "The member has been removed from the organization",
      });

      fetchMembers();
    } catch (error) {
      console.error("Error removing member:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove member",
      });
    }
  };

  if (!currentOrganization) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Organization Members</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <p>Please select an organization first.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Organization Members</CardTitle>
        <Button
          onClick={() => setIsInviteDialogOpen(true)}
          size="sm"
          className="rounded-none"
        >
          <UserPlus className="h-4 w-4 mr-2" /> Invite Member
        </Button>
      </CardHeader>
      <CardContent>
        {loadingMembers ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {members.length === 0 && invitations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No members in this organization yet.</p>
                <p className="text-sm mt-2">
                  Invite team members to collaborate.
                </p>
                <Button
                  onClick={() => setIsInviteDialogOpen(true)}
                  variant="outline"
                  className="mt-4"
                >
                  Invite Member
                </Button>
              </div>
            ) : (
              <Tabs defaultValue="members" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4 rounded-none">
                  <TabsTrigger value="members">Members</TabsTrigger>
                  <TabsTrigger value="invitations">Invitations</TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="space-y-4">
                  {members.length > 0 ? (
                    <div className="space-y-3">
                      {members.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between border rounded-none p-3"
                        >
                          <div>
                            <p className="font-medium">{member.users.email}</p>
                            <p className="text-xs text-muted-foreground">
                              Joined{" "}
                              {new Date(member.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {member.users.id === currentUser?.id ? (
                              <Badge className="bg-primary text-primary-foreground">
                                {member.role}
                              </Badge>
                            ) : (
                              <Select
                                defaultValue={member.role}
                                onValueChange={(value) =>
                                  updateMemberRole(member.id, value)
                                }
                              >
                                <SelectTrigger className="w-[110px] rounded-none">
                                  <SelectValue placeholder="Role" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="owner">Owner</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  <SelectItem value="member">Member</SelectItem>
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                </SelectContent>
                              </Select>
                            )}

                            {member.users.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeMember(member.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No active members yet.</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="invitations" className="space-y-4">
                  {/* Pending invitations */}
                  {invitations.filter((inv) => inv.status === "pending")
                    .length > 0 ? (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium mb-3">
                        Pending Invitations
                      </h3>
                      {invitations
                        .filter((inv) => inv.status === "pending")
                        .map((invitation) => (
                          <div
                            key={invitation.id}
                            className="flex items-center justify-between border rounded-none p-3 bg-muted/30"
                          >
                            <div>
                              <div className="flex items-center">
                                <p className="font-medium">
                                  {invitation.email}
                                </p>
                                <Badge
                                  variant="outline"
                                  className="ml-2 rounded-none"
                                >
                                  Pending
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Invited{" "}
                                {new Date(
                                  invitation.created_at,
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge>{invitation.role}</Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const invitationLink = `${window.location.origin}/invite-accept?token=${invitation.token}`;
                                  navigator.clipboard.writeText(invitationLink);
                                  setCopiedId(invitation.id);
                                  setTimeout(() => setCopiedId(null), 2000);
                                  toast({
                                    title: "Copied",
                                    description:
                                      "Invitation link copied to clipboard",
                                  });
                                }}
                                title="Copy invitation link"
                              >
                                {copiedId === invitation.id ? (
                                  <Check className="h-4 w-4 text-good" />
                                ) : (
                                  <Copy className="h-4 w-4 text-muted-foreground" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => cancelInvitation(invitation.id)}
                                title="Cancel invitation"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No pending invitations.</p>
                      <Button
                        onClick={() => setIsInviteDialogOpen(true)}
                        variant="outline"
                        className="mt-4"
                      >
                        Invite Member
                      </Button>
                    </div>
                  )}

                  {/* Accepted invitations */}
                  {invitations.filter((inv) => inv.status === "accepted")
                    .length > 0 && (
                    <div className="space-y-3 mt-6">
                      <h3 className="text-sm font-medium mb-3">
                        Accepted Invitations
                      </h3>
                      {invitations
                        .filter((inv) => inv.status === "accepted")
                        .map((invitation) => (
                          <div
                            key={invitation.id}
                            className="flex items-center justify-between border rounded-md p-3 bg-good-bg/30"
                          >
                            <div>
                              <div className="flex items-center">
                                <p className="font-medium">
                                  {invitation.email}
                                </p>
                                <Badge
                                  variant="outline"
                                  className="ml-2 bg-good-bg text-good border-good-line"
                                >
                                  Accepted
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Invited{" "}
                                {new Date(
                                  invitation.created_at,
                                ).toLocaleDateString()}
                                {invitation.accepted_at && (
                                  <>
                                    {" "}
                                    • Accepted{" "}
                                    {new Date(
                                      invitation.accepted_at,
                                    ).toLocaleDateString()}
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge>{invitation.role}</Badge>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Expired invitations */}
                  {invitations.filter((inv) => inv.status === "expired")
                    .length > 0 && (
                    <div className="space-y-3 mt-6">
                      <h3 className="text-sm font-medium mb-3">
                        Expired Invitations
                      </h3>
                      {invitations
                        .filter((inv) => inv.status === "expired")
                        .map((invitation) => (
                          <div
                            key={invitation.id}
                            className="flex items-center justify-between border rounded-md p-3 bg-muted/30"
                          >
                            <div>
                              <div className="flex items-center">
                                <p className="font-medium">
                                  {invitation.email}
                                </p>
                                <Badge
                                  variant="outline"
                                  className="ml-2 bg-muted text-foreground border-border"
                                >
                                  Expired
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Invited{" "}
                                {new Date(
                                  invitation.created_at,
                                ).toLocaleDateString()}
                                {invitation.expires_at && (
                                  <>
                                    {" "}
                                    • Expired{" "}
                                    {new Date(
                                      invitation.expires_at,
                                    ).toLocaleDateString()}
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setInviteEmail(invitation.email);
                                  setInviteRole(invitation.role as any);
                                  setIsInviteDialogOpen(true);
                                }}
                              >
                                Resend
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => cancelInvitation(invitation.id)}
                                title="Remove invitation"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </>
        )}
      </CardContent>

      {/* Invite dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as typeof inviteRole)}>
                <SelectTrigger id="role" className="rounded-none">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsInviteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={inviteMember}
              disabled={!inviteEmail.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
